import { boxFrom, elementAt, isTap, scaleBox, simplify, toImagePoint } from './geometry.js';
import { draftSvg, hoverSvg, marksSvg } from './render.js';
const SVG_NS = 'http://www.w3.org/2000/svg';
/** A region or a box smaller than this, in image pixels, was a slip rather than a mark. */
const MIN_SIZE = 4;
export function createAnnotator(host, initial) {
    let options = { ...initial };
    const svg = document.createElementNS(SVG_NS, 'svg');
    svg.setAttribute('class', 'annotate-layer');
    svg.setAttribute('preserveAspectRatio', 'none');
    svg.style.position = 'absolute';
    svg.style.inset = '0';
    svg.style.width = '100%';
    svg.style.height = '100%';
    svg.style.userSelect = 'none';
    const marksLayer = document.createElementNS(SVG_NS, 'g');
    const hoverLayer = document.createElementNS(SVG_NS, 'g');
    const draftLayer = document.createElementNS(SVG_NS, 'g');
    svg.append(hoverLayer, marksLayer, draftLayer);
    host.append(svg);
    /** The gesture in progress: where it started on screen and in the image, and what it has drawn so far. */
    let press = null;
    let draft = null;
    const pointers = new Set();
    /** Image pixels per screen pixel right now, so marks stay readable on a small screen. */
    const pixelsPerScreenPixel = () => {
        const shown = svg.getBoundingClientRect().width;
        return shown > 0 ? options.width / shown : 1;
    };
    const sizeOf = () => ({ width: options.width, height: options.height, selected: options.selected, pixelsPerScreenPixel: pixelsPerScreenPixel() });
    const at = (event) => {
        const r = svg.getBoundingClientRect();
        return toImagePoint({ x: event.clientX, y: event.clientY }, r, options);
    };
    const render = () => {
        svg.setAttribute('viewBox', `0 0 ${options.width} ${options.height}`);
        svg.style.cursor = options.mode === 'view' ? 'default' : 'crosshair';
        // One finger draws; the browser keeps pinch-zoom (the second finger cancels the draft).
        svg.style.touchAction = options.mode === 'view' ? 'auto' : 'pinch-zoom';
        marksLayer.innerHTML = marksSvg(options.marks, sizeOf());
        draftLayer.innerHTML = draftSvg(draft, sizeOf());
    };
    const showHover = (p) => {
        const i = p && options.mode === 'annotate' ? elementAt(options.elements, p, options.elementScale ?? 1) : undefined;
        const el = i === undefined ? undefined : options.elements?.[i];
        hoverLayer.innerHTML = el ? hoverSvg(scaleBox(el.box, options.elementScale ?? 1), sizeOf()) : '';
    };
    const cancel = () => {
        press = null;
        draft = null;
        draftLayer.innerHTML = '';
    };
    const markUnder = (event) => {
        const badge = event.target?.closest?.('[data-mark-id]');
        return badge?.getAttribute('data-mark-id') ?? null;
    };
    const onDown = (event) => {
        pointers.add(event.pointerId);
        if (pointers.size > 1) {
            cancel(); // a second finger is zooming, not drawing
            return;
        }
        if (event.button !== 0 && event.pointerType === 'mouse')
            return;
        const picked = markUnder(event);
        if (picked || options.mode === 'view') {
            options.onSelect?.(picked);
            return;
        }
        event.preventDefault();
        svg.setPointerCapture?.(event.pointerId);
        const start = at(event);
        press = { pointer: event.pointerId, screen: { x: event.clientX, y: event.clientY }, start, points: [start] };
    };
    const onMove = (event) => {
        if (!press || press.pointer !== event.pointerId) {
            if (event.pointerType === 'mouse')
                showHover(at(event));
            return;
        }
        const p = at(event);
        if (options.mode === 'annotate') {
            draft = isTap(press.screen, { x: event.clientX, y: event.clientY }) ? null : { kind: 'region', box: boxFrom(press.start, p) };
        }
        else if (options.tool === 'pen') {
            press.points.push(p);
            draft = { kind: 'stroke', tool: 'pen', points: press.points };
        }
        else if (options.tool === 'arrow' || options.tool === 'box') {
            draft = { kind: 'stroke', tool: options.tool, points: [press.start, p] };
        }
        draftLayer.innerHTML = draftSvg(draft, sizeOf());
    };
    const onUp = (event) => {
        pointers.delete(event.pointerId);
        if (!press || press.pointer !== event.pointerId)
            return;
        const end = at(event);
        const tapped = isTap(press.screen, { x: event.clientX, y: event.clientY });
        const start = press.start;
        const points = press.points;
        cancel();
        let made = null;
        if (options.mode === 'annotate') {
            if (tapped) {
                const element = elementAt(options.elements, start, options.elementScale ?? 1);
                made = { geometry: { kind: 'pin', at: start }, ...(element === undefined ? {} : { element }) };
            }
            else {
                const box = boxFrom(start, end);
                if (box.w >= MIN_SIZE && box.h >= MIN_SIZE)
                    made = { geometry: { kind: 'region', box } };
            }
        }
        else if (options.mode === 'draw') {
            const tool = options.tool;
            if (tool === 'text') {
                if (tapped)
                    made = { geometry: { kind: 'stroke', tool, points: [start], label: '' } };
            }
            else if (tool === 'pen') {
                made = { geometry: { kind: 'stroke', tool, points: tapped ? [start] : simplify([...points, end]) } };
            }
            else {
                const box = boxFrom(start, end);
                if (!tapped && Math.max(box.w, box.h) >= MIN_SIZE)
                    made = { geometry: { kind: 'stroke', tool, points: [start, end] } };
            }
        }
        if (made)
            options.onCreate?.(made);
    };
    const onCancel = (event) => {
        pointers.delete(event.pointerId);
        if (press?.pointer === event.pointerId)
            cancel();
    };
    const onLeave = () => showHover(null);
    const onKey = (event) => {
        if (event.key === 'Escape' && press)
            cancel();
    };
    // A window resized or a phone turned changes how big the snapshot is shown.
    const resized = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(() => render());
    resized?.observe(svg);
    svg.addEventListener('pointerdown', onDown);
    svg.addEventListener('pointermove', onMove);
    svg.addEventListener('pointerup', onUp);
    svg.addEventListener('pointercancel', onCancel);
    svg.addEventListener('pointerleave', onLeave);
    window.addEventListener('keydown', onKey);
    render();
    return {
        update(next) {
            const modeChanged = next.mode !== undefined && next.mode !== options.mode;
            options = { ...options, ...next };
            if (modeChanged) {
                cancel();
                showHover(null);
            }
            render();
        },
        destroy() {
            window.removeEventListener('keydown', onKey);
            resized?.disconnect();
            svg.remove();
        },
    };
}
//# sourceMappingURL=annotator.js.map